import type { ReactiveController, ReactiveControllerHost } from 'lit';

export interface OverflowExpandHost extends ReactiveControllerHost, HTMLElement {
  /** Reflects whether the control is currently expanded to show clipped text. */
  expandedOverflow: boolean;
}

export interface OverflowExpandOptions {
  /** Returns the shell that visually represents the control (e.g. `.input`, `.select__combobox`). */
  getShell: () => HTMLElement | null | undefined;
  /** Returns the clipped text element (e.g. `.input__control`, `.select__display-input`). */
  getTextElement: () => HTMLElement | null | undefined;
  /**
   * Optional gate. When it returns false the expansion never opens
   * (e.g. while a select listbox is open, or for multi-select).
   */
  canExpand?: () => boolean;
  /** Max width as a fraction of the viewport. Default 0.92. */
  maxViewportFraction?: number;
  /** Dwell delay (ms) before expanding on pointer enter. Default 120. */
  dwellMs?: number;
}

interface ExpandGeometry {
  anchorRect: DOMRect;
  left: number;
  width: number;
  maxWidth: number;
}

/**
 * Expands a clipped form control in place on hover/focus so the full value is
 * readable. Measures overflow only at interaction time (no cached state) and
 * positions a fixed overlay from the control's bounding rect so scrolling panels
 * and overflow:hidden ancestors cannot clip it.
 *
 * Layout is preserved by pinning the internal `.form-control-input` holder while
 * expanded; the shell itself is taken out of flow with position:fixed.
 */
export class OverflowExpandController implements ReactiveController {
  private readonly host: OverflowExpandHost;
  private readonly options: Required<
    Pick<OverflowExpandOptions, 'getShell' | 'getTextElement' | 'maxViewportFraction' | 'dwellMs'>
  > &
    Pick<OverflowExpandOptions, 'canExpand'>;

  private shell: HTMLElement | null = null;
  private dwellTimer = 0;
  private pinnedHolder: HTMLElement | null = null;
  private pinnedMinHeight = '';
  private bound = false;
  private dismissAttached = false;
  /** Geometry captured before the shell leaves the flow. */
  private geometry: ExpandGeometry | null = null;

  private readonly onPointerEnter = () => this.scheduleExpand();
  private readonly onPointerLeave = () => this.handlePointerLeave();
  private readonly onFocusIn = () => this.tryExpand();
  private readonly onFocusOut = () => this.handleFocusOut();
  private readonly onScroll = () => this.collapse();
  private readonly onResize = () => this.collapse();
  private readonly onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') this.collapse();
  };

  constructor(host: OverflowExpandHost, options: OverflowExpandOptions) {
    this.host = host;
    this.options = {
      getShell: options.getShell,
      getTextElement: options.getTextElement,
      canExpand: options.canExpand,
      maxViewportFraction: options.maxViewportFraction ?? 0.92,
      dwellMs: options.dwellMs ?? 120
    };
    host.addController(this);
  }

  hostConnected() {
    // Listeners bind after first render once the shell exists in the shadow tree.
  }

  hostUpdated() {
    this.bindShell();
  }

  hostDisconnected() {
    this.collapse();
    this.unbindShell();
  }

  /** Force-close the expansion (e.g. when a select listbox opens). */
  collapse() {
    this.clearDwell();
    this.detachDismissListeners();
    this.geometry = null;
    if (!this.host.expandedOverflow) {
      this.clearShellInlinePosition();
      return;
    }
    this.host.expandedOverflow = false;
    this.clearPinnedHolder();
    this.clearShellInlinePosition();
  }

  private bindShell() {
    const next = this.options.getShell() ?? null;
    if (next === this.shell && this.bound) return;

    this.unbindShell();
    this.shell = next;
    if (!this.shell) return;

    this.shell.addEventListener('pointerenter', this.onPointerEnter);
    this.shell.addEventListener('pointerleave', this.onPointerLeave);
    this.shell.addEventListener('focusin', this.onFocusIn);
    this.shell.addEventListener('focusout', this.onFocusOut);
    this.bound = true;
  }

  private unbindShell() {
    if (this.shell) {
      this.shell.removeEventListener('pointerenter', this.onPointerEnter);
      this.shell.removeEventListener('pointerleave', this.onPointerLeave);
      this.shell.removeEventListener('focusin', this.onFocusIn);
      this.shell.removeEventListener('focusout', this.onFocusOut);
    }
    this.shell = null;
    this.bound = false;
    this.clearDwell();
  }

  private scheduleExpand() {
    this.clearDwell();
    this.dwellTimer = window.setTimeout(() => this.tryExpand(), this.options.dwellMs);
  }

  private clearDwell() {
    if (this.dwellTimer) {
      window.clearTimeout(this.dwellTimer);
      this.dwellTimer = 0;
    }
  }

  private handlePointerLeave() {
    this.clearDwell();
    // Keep expanded while the control still has focus so the caret does not jump.
    if (this.hostContainsFocus()) return;
    this.collapse();
  }

  private handleFocusOut() {
    // Defer so focus moving to a sibling inside the shell does not collapse.
    requestAnimationFrame(() => {
      if (this.hostContainsFocus() || this.shellIsHovered()) return;
      this.collapse();
    });
  }

  private hostContainsFocus(): boolean {
    const root = this.host.shadowRoot;
    const active = (root?.activeElement as Node | null) ?? null;
    if (active && this.shell?.contains(active)) return true;
    return this.host.matches(':focus-within');
  }

  private shellIsHovered(): boolean {
    return !!this.shell?.matches(':hover');
  }

  private tryExpand() {
    this.clearDwell();
    if (this.host.expandedOverflow) {
      this.applyGeometry();
      return;
    }
    if (this.options.canExpand && !this.options.canExpand()) return;

    const shell = this.options.getShell();
    const textEl = this.options.getTextElement();
    if (!shell || !textEl) return;

    // Horizontal clip only - a single-line control cannot reveal anything by growing taller.
    const hidden = textEl.scrollWidth - textEl.clientWidth;
    if (hidden <= 1) return;

    const geometry = this.computeGeometry(shell, textEl);
    if (!geometry) return;

    // Capture geometry before the shell leaves the flow, then pin the internal holder
    // (not the host) so nothing escapes onto the public style attribute.
    this.geometry = geometry;
    this.pinHolder(geometry.anchorRect);
    this.host.expandedOverflow = true;
    this.attachDismissListeners();
    // Position after the class/state update so styles that depend on the flag apply.
    void this.host.updateComplete.then(() => this.applyGeometry());
  }

  private getAnchorElement(): HTMLElement {
    // Prefer the form-control-input box when present so left-label layouts
    // expand over the value column, not the full host including the label.
    return (this.host.shadowRoot?.querySelector('.form-control-input') as HTMLElement | null) ?? this.host;
  }

  /**
   * Compute the expanded width and side before committing state. Returns null when
   * the target width would not exceed the current anchor width (nothing to reveal).
   */
  private computeGeometry(shell: HTMLElement, textEl: HTMLElement): ExpandGeometry | null {
    const anchorRect = this.getAnchorElement().getBoundingClientRect();
    // Everything the box needs on top of the text itself: borders, padding, prefix and
    // suffix slots, clear button and their margins. offsetWidth is the border box;
    // clientWidth is the padding box of the clipped text element.
    const chrome = shell.offsetWidth - textEl.clientWidth;
    const wanted = Math.ceil(textEl.scrollWidth + chrome);
    const maxWidth = Math.floor(window.innerWidth * this.options.maxViewportFraction);
    const edge = 8;

    const roomRight = Math.max(0, window.innerWidth - edge - anchorRect.left);
    const roomLeft = Math.max(0, anchorRect.right - edge);
    const growLeft = wanted > roomRight && roomLeft > roomRight;
    const room = growLeft ? roomLeft : roomRight;
    const width = Math.min(wanted, room, maxWidth);

    // Nothing useful to reveal - do not flip state or pin layout.
    if (width <= anchorRect.width + 1) return null;

    const left = growLeft ? anchorRect.right - width : anchorRect.left;
    return { anchorRect, left, width, maxWidth };
  }

  private pinHolder(anchorRect: DOMRect) {
    const holder = this.getAnchorElement();
    this.pinnedHolder = holder;
    this.pinnedMinHeight = holder.style.minHeight;
    // Preserve in-flow layout height so the form does not reflow on expand.
    holder.style.minHeight = `${anchorRect.height}px`;
  }

  private clearPinnedHolder() {
    if (this.pinnedHolder) {
      this.pinnedHolder.style.minHeight = this.pinnedMinHeight;
    }
    this.pinnedHolder = null;
    this.pinnedMinHeight = '';
  }

  private clearShellInlinePosition() {
    const shell = this.options.getShell();
    if (!shell) return;
    shell.style.position = '';
    shell.style.top = '';
    shell.style.left = '';
    shell.style.width = '';
    shell.style.maxWidth = '';
    shell.style.zIndex = '';
    shell.style.boxSizing = '';
  }

  private applyGeometry() {
    const shell = this.options.getShell();
    if (!shell || !this.host.expandedOverflow || !this.geometry) return;

    const { anchorRect, left, width, maxWidth } = this.geometry;
    shell.style.position = 'fixed';
    shell.style.top = `${anchorRect.top}px`;
    shell.style.left = `${left}px`;
    shell.style.width = `${width}px`;
    shell.style.maxWidth = `${maxWidth}px`;
    shell.style.zIndex = 'var(--webmodule-z-tooltip, 1000)';
    shell.style.boxSizing = 'border-box';
  }

  private attachDismissListeners() {
    if (this.dismissAttached) return;
    window.addEventListener('scroll', this.onScroll, true);
    window.addEventListener('resize', this.onResize);
    window.addEventListener('keydown', this.onKeyDown, true);
    this.dismissAttached = true;
  }

  private detachDismissListeners() {
    if (!this.dismissAttached) return;
    window.removeEventListener('scroll', this.onScroll, true);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('keydown', this.onKeyDown, true);
    this.dismissAttached = false;
  }
}
